/**
 * Runtime hydration: bootstraps API + session state, then refreshes the home.
 * Extracted from PrototypeController to keep the controller focused on event
 * composition and below the architecture governance line cap.
 */

import { hideLoginState, promptSessionReLogin, showLoginState } from "./authGateController";
import { failValidation, recordRuntimeError } from "./prototypeRuntimeSupport";
import { startVideoWatermark, stopVideoWatermark } from "./videoWatermark";
import { previewUpgrade } from './commerceController';
import {
	mountSubscriptionChooser,
	setMountedSubscriptionChooserCapability,
	updateSubscriptionChooser,
	subscriptionChooserContextFromState,
} from './subscriptionChooserController';
import { planStore } from "./planStore";
import { invalidateTopicIndex } from "./topicStore";
import { syncTopicViewerCacheScope } from './topicViewerCacheScope';
import { siteSettingsStore } from "./siteSettingsStore";
import { categoriesStore } from "./categoriesStore";
import { hydrateCategoryButtons } from './categoryButtonsHydrator';
import { hydrateActiveHome, isHomeViewActive } from './homeViewHydrator';
import { byId } from "./domUtils";
import { applyInitialViewFromUrl, closeAuthenticationModals, showView } from "./navigationController";
import { resolveInitialRoute } from './initialRoute';
import { readPageModel } from './pageModelReader';
import { getRuntime } from './runtimeAccessor';
import type { PlanCode, PrototypeRuntime, Root } from "./prototypeTypes";
import { entitlementRefresh, setEntitlementRefreshRuntime } from "./entitlementRefresh/entitlementRefresh";
import type { PendingChange, SessionSnapshot } from './entitlementRefresh/refreshTypes';
import { reconcileActiveModalTurnstile } from './modalTurnstileLifecycle';
import { applyTurnstilePublicConfigForGeneration, beginTurnstilePublicConfigRequest, type TurnstilePublicConfig } from './turnstileRuntime';
import { replayPendingCheckoutIntent } from './pendingCheckoutIntent';
import { preservesPrivateAuthModal } from './initialRoute';
import { feedbackTicketIdFromBrowserLocation } from './notificationActionRoute';
import { activateFeedbackTab, loadMyFeedback, openFeedbackDetail, switchToFeedbackTab } from './actionRoutes/profileFeedbackTicketActions';
import { parseClientPlanContext, unauthenticatedClientPlanContext, type ClientPlanContext } from './clientPlanContext';
import { hasPendingRedeemAuthenticationResume } from './pendingRedeemIntent';
import { schedulePostUpgradePageRefresh } from './postUpgradeRefresh';
import { consumeLoginAuthIntent } from './authIntent';
import { recordSessionCheckGaveUp, recordSessionCheckRecovered } from './clientSessionDiagnostics';

interface SessionPayload {
  viewer: {
    authStatus: "visitor" | "authenticated";
    plan: PlanCode;
    userId: string | null;
    email: string | null;
    displayName?: string;
    sessionHash?: string | null;
		planContext?: unknown;
  };
  subscription?: { plan?: PlanCode } | null;
  /** Set when the server recognized a revoked credential, cleared its cookie,
   *  and answered as a visitor — the client must prompt re-login. */
  sessionRevoked?: boolean;
}

/** Extra immediate hydration re-runs after a failed Home session check before
 *  the manual Retry chip appears. Fresh cross-border connections fail in short
 *  bursts; one dropped request must not freeze Home on skeletons. */
const HOME_SESSION_AUTO_RETRIES = 2;
const hydrationGenerations = new WeakMap<object, number>();

interface HydrationOptions {
  preserveSsrView?: boolean;
  /** Auto-retry rounds already spent for this hydration chain (internal). */
  sessionAutoRetryAttempt?: number;
}

export interface PrototypeState {
  runtime: PrototypeRuntime;
  isLoggedIn: boolean;
	/** Current paid billing tier only. Never use for access decisions. */
  currentPlan: PlanCode;
	/** Current capability tier after paid and temporary grants are merged. */
	effectiveAccessPlan: PlanCode;
	/** Null before an authenticated Session response passes validation. */
	planContext: ClientPlanContext | null;
}

export async function hydratePrototypeRuntime(
  root: Root,
  state: PrototypeState,
  options: HydrationOptions = {},
): Promise<void> {
	resetCrossCycleUpgradeCapability(root, state);
  const preserveSsrView = options.preserveSsrView === true;
  const startup = beginStartupSnapshot(root, state);
	const cloudStartup = !isLocalStartupOrigin();
	// Cloud requests can overlap, but provider installation remains below at its
	// original consumption point. Local keeps session-first bootstrap because
	// its development fallback still uses the client plan hint.
	const sessionRequest = state.runtime.api.getSession();
	const initialBootstrapRequest = cloudStartup ? state.runtime.api.bootstrap(startup.effectiveAccessPlan) : null;
	const initialPublicConfigGeneration = cloudStartup ? beginTurnstilePublicConfigRequest(state.runtime) : null;
	const initialPublicConfigRequest = cloudStartup ? state.runtime.api.publicConfig() : null;
	initialBootstrapRequest?.catch(() => undefined);
	initialPublicConfigRequest?.catch(() => undefined);
	let sessionResult = (await Promise.allSettled([sessionRequest]))[0];
	if (!ownsStartupSnapshot(root, state, startup)) return;
  if (sessionResult.status !== 'fulfilled' || !sessionResult.value?.viewer) {
    // A rejected/malformed session check is not evidence that this user is a
    // visitor. On Home, retain the pending member frame and provide an
    // in-place retry instead of replacing it with the much taller guest hero.
    // The first attempts are automatic: a single dropped fresh connection must
    // not freeze Home on skeletons until the user notices the tiny Retry chip.
    // Only once the auto budget is spent does the manual chip appear.
    if (!preserveSsrView && isActiveHomeRoute(root)) {
      const attempt = options.sessionAutoRetryAttempt ?? 0;
      if (attempt < HOME_SESSION_AUTO_RETRIES) {
        return hydratePrototypeRuntime(root, state, { ...options, sessionAutoRetryAttempt: attempt + 1 });
      }
      recordSessionCheckGaveUp(attempt);
      retainHomeFirstFrameForSessionRetry(root, state, options);
    }
    return;
  }
	let sessionPayload = sessionResult.value as SessionPayload;
	let v = sessionPayload.viewer;
	let resolvedPlanContext = v.authStatus === 'authenticated' ? parseClientPlanContext(v.planContext) : unauthenticatedClientPlanContext();
	let resolvedAccessPlan: PlanCode = resolvedPlanContext?.effectiveAccessPlan ?? (v.authStatus === 'authenticated' ? 'free' : 'visitor');
	const bootstrapRequest = initialBootstrapRequest ?? state.runtime.api.bootstrap(resolvedAccessPlan);
	const publicConfigGeneration = initialPublicConfigGeneration ?? beginTurnstilePublicConfigRequest(state.runtime);
	const publicConfigRequest = initialPublicConfigRequest ?? state.runtime.api.publicConfig();
	let [bootstrapResult, publicConfigResult] = await Promise.allSettled([bootstrapRequest, publicConfigRequest]);
	if (!ownsStartupSnapshot(root, state, startup)) return;
	// A successful bootstrap whose viewer differs from the session can occur
	// when login, logout, or a plan grant lands between the two requests. Read
	// exactly one authoritative session -> bootstrap pair in the original order.
	// A first bootstrap network failure deliberately keeps the existing fallback:
	// it is diagnosed later but must not turn a valid session into a retry loop.
	if (bootstrapResult.status === 'fulfilled' && !bootstrapMatchesSession(bootstrapResult.value, v)) {
		if (!ownsStartupSnapshot(root, state, startup)) return;
		const rereadSessionResult = (await Promise.allSettled([state.runtime.api.getSession()]))[0];
		if (!ownsStartupSnapshot(root, state, startup)) return;
		if (rereadSessionResult.status !== 'fulfilled' || !rereadSessionResult.value?.viewer) {
			if (rereadSessionResult.status === 'rejected') recordRuntimeError(state.runtime, rereadSessionResult.reason);
			return retainHomeFirstFrameForStartupMismatch(root, state, options);
		}
		const rereadPayload = rereadSessionResult.value as SessionPayload;
		const rereadViewer = rereadPayload.viewer;
		const rereadPlanContext = rereadViewer.authStatus === 'authenticated' ? parseClientPlanContext(rereadViewer.planContext) : unauthenticatedClientPlanContext();
		const rereadAccessPlan: PlanCode = rereadPlanContext?.effectiveAccessPlan ?? (rereadViewer.authStatus === 'authenticated' ? 'free' : 'visitor');
		const rereadBootstrapResult = (await Promise.allSettled([state.runtime.api.bootstrap(rereadAccessPlan)]))[0];
		if (!ownsStartupSnapshot(root, state, startup)) return;
		if (rereadBootstrapResult.status === 'fulfilled' && !bootstrapMatchesSession(rereadBootstrapResult.value, rereadViewer)) {
			return retainHomeFirstFrameForStartupMismatch(root, state, options);
		}
		sessionResult = rereadSessionResult;
		bootstrapResult = rereadBootstrapResult;
		sessionPayload = rereadPayload;
		v = rereadViewer;
		resolvedPlanContext = rereadPlanContext;
		resolvedAccessPlan = rereadAccessPlan;
	}
  setHomeWorkspaceBusy(root, false);
  // A successful session check is the delivery moment for any evidence the
  // diagnostics recorder buffered during an earlier outage window.
  recordSessionCheckRecovered();
	state.planContext = v.authStatus === 'authenticated' ? resolvedPlanContext : null;
	state.currentPlan = v.authStatus === 'authenticated' ? resolvedPlanContext?.billingPlan ?? 'free' : 'visitor';
	state.effectiveAccessPlan = resolvedAccessPlan;
	state.runtime.currentPlan = resolvedAccessPlan;
	// The server answered this browser's stale credential with "you are a
	// visitor now" and cleared the cookie — settle the guest Home, then open
	// the login modal so the user can sign back in (fourth-device evictions
	// and password rotations land here).
	if (sessionPayload.sessionRevoked === true) {
		promptSessionReLogin(root, state);
	}
  // 回站刷新: URL 含 ?checkout=success 时触发 session 刷新(前端权益刷新设计 §3.1)
  if (typeof URLSearchParams !== "undefined" && new URLSearchParams(globalThis.location?.search ?? "").get("checkout") === "success") {
    void entitlementRefresh.refreshSession(state.runtime);
  }
	const boot = bootstrapResult.status === 'fulfilled'
		? installBootstrapPayload(root, state.runtime, bootstrapResult.value, { userId: v.userId, sessionHash: v.sessionHash ?? null })
		: null;
	if (boot) {
		const viewer = boot.viewer as { userId: string | null; email?: string | null };
		if (viewer.userId) state.runtime.userId = viewer.userId;
		if (viewer.email) state.runtime.userEmail = viewer.email;
		// Plan + category + site-settings hydration is installed only after
		// the bootstrap viewer identity matches the authoritative session.
	}
  if (sessionResult.status === "fulfilled" && sessionResult.value?.viewer) {
    if (v.userId) state.runtime.userId = v.userId;
    if (v.email) state.runtime.userEmail = v.email;
    if (v.displayName) state.runtime.userDisplayName = v.displayName;
    if (v.sessionHash) state.runtime.sessionHash = v.sessionHash;
    if (v.authStatus === "authenticated") {
      hideLoginState(root, state);
      // Close any auth modal that may still be open. This matters
      // for the OAuth round-trip: the user clicked Google login from the
      // login modal, was redirected to Google, then 302'd back to
      // `/`. The full page reload re-renders the markup, but the modal
      // could still be `.active` / not `.hidden` in the milliseconds before
      // hydration runs. This call is the safety net — mirroring what the
      // email/password path does in authenticate() at authActions.ts:83-84.
      if (!preservesPrivateAuthModal(window.location.pathname)) closeAuthenticationModals(root);
      // Refresh plan from the latest session payload — overrides the
      // resolvedPlan we set above if the subscription carries a
      // different tier (rare, but the auth/session endpoint is the
      // source of truth for the currently active plan).
		state.currentPlan = resolvedPlanContext?.billingPlan ?? 'free';
		state.effectiveAccessPlan = resolvedAccessPlan;
		state.runtime.currentPlan = resolvedAccessPlan;
			syncTopicViewerCacheScope(state.runtime, true);
      invalidateTopicIndex();
      // The avatar mark (initials) + logged-in dropdown are painted by
      // hideLoginState -> syncAvatarMenuState -> renderAvatarDropdown ->
      // paintAvatar, using state.runtime.userDisplayName / userEmail /
      // userId (all populated above BEFORE hideLoginState runs). Do NOT
      // add a second writer to #avatarInitials / #avatarBtn here — that
      // multi-writer pattern is exactly what caused the recurring
      // "avatar blank / stale after OAuth" regression. Any avatar change
      // must go through avatarView.ts (see its header).
      // refreshHomeState for authenticated users is intentionally NOT
      // called here — line 151 below runs `refreshHomeState` once for
      // both visitor and authenticated paths after every other home
      // hydrator has settled. Calling it twice (once here, once below)
      // doubled paint-time fan-out: hydrateHomeRecommendations +
      // hydrateResumeCard fired twice per home load.
    }
  }
	setEntitlementRefreshRuntime(state.runtime, (snapshot, change) => applyConfirmedBillingSnapshot(root, state, snapshot, change));
  // The server shell starts with visitor-only chrome hidden to avoid an auth
  // flash. Authenticated hydration calls hideLoginState above; the visitor
  // branch must symmetrically reveal its CTA and logged-out avatar state.
  // Previously it only refreshed the home body, leaving #navPreviewCta at the
  // shell's permanent `display:none` despite a confirmed visitor session.
  // When an inline session model seeded isLoggedIn=true but the async
  // session resolves to visitor (cookie expired between server render and
  // client hydration), we must downgrade to visitor state explicitly.
  const asyncVisitor = !v || v.authStatus !== "authenticated";
  if (!state.isLoggedIn || asyncVisitor) showLoginState(root, state);
	// Landing can request a login prompt with exactly `?auth=login`. Only
	// consume it once the authoritative session has settled and the current
	// visitor/member chrome has been applied; failed, malformed and stale
	// session reads intentionally leave the URL and modal untouched.
	consumeLoginAuthIntent(root, v.authStatus);
  for (const result of [bootstrapResult, publicConfigResult]) {
    if (result.status === "rejected") recordRuntimeError(state.runtime, result.reason);
  }
  // Apply the Turnstile site key from publicConfig so the client gets real
  // challenge tokens. Before this fix, the runtime always used the dev token
  // ("prototype-dev-token") because the site key was fetched but never applied.
  // Both the invisible provider (for auth/feedback) and the interactive provider
  // (for visible AI Create / Submit Idea widgets) are upgraded with the real key.
  if (publicConfigResult.status === "fulfilled") {
		try {
			applyTurnstilePublicConfigForGeneration(state.runtime, publicConfigResult.value as TurnstilePublicConfig, publicConfigGeneration);
			void reconcileActiveModalTurnstile(root);
		} catch (error) {
			recordRuntimeError(state.runtime, error);
		}
  }
  if (state.runtime.userId && state.runtime.userEmail) {
    startVideoWatermark(root);
  } else {
    stopVideoWatermark();
  }
	const api = state.runtime.api;
	if (!preserveSsrView && isHomeViewActive(root)) void hydrateActiveHome(root, state);
	mountPublicSubscriptionChoosers(root, state);
  // Re-apply URL routing now that the real session and plan are resolved.
  // The first pass only activates the URL's view; this pass performs the
  // route-specific fetch exactly once with the authenticated access plan.
	if ((!preserveSsrView || shouldResumeChapterAfterSsrCourseNavigation(root)) && !isHomeViewActive(root)) applyInitialViewFromUrl(root, state);
	// A same-origin notification link is deliberately strict: one valid UUID
	// parameter only. Once the real session is known, open its exact My
	// Feedback conversation; invalid or extra query parameters remain inert.
	const feedbackTicketId = feedbackTicketIdFromBrowserLocation(window.location);
	if (state.isLoggedIn && feedbackTicketId) {
		switchToFeedbackTab(root, 'my');
		activateFeedbackTab(root, 'my');
		if (await loadMyFeedback(root, state)) await openFeedbackDetail(root, state, feedbackTicketId);
	}
	if (state.isLoggedIn) {
		if (hasPendingRedeemAuthenticationResume(state.runtime.userId)) {
			window.history.replaceState({}, '', '/redeem');
			showView('redeem', root);
		}
		await replayPendingCheckoutIntent(state.runtime.userId, async (plan, billingCycle, source) => {
			const outcome = await previewUpgrade(plan, root, state, recordRuntimeError, { source, billingCycle });
			if (!outcome.ok && 'message' in outcome) failValidation(outcome.message);
		});
	}
}

/**
 * A server-rendered course page initially preserves its DOM while auth
 * hydration resolves. A user can open another Chapter before that identity is
 * ready. The first Chapter response is correctly rejected once the viewer
 * scope changes, so resume only that current Chapter route after hydration.
 *
 * Do not rehydrate an unchanged SSR Topic or Chapter: both already have their
 * authoritative server body and must retain their first paint.
 */
export function shouldResumeChapterAfterSsrCourseNavigation(root: Root): boolean {
	if (typeof window === 'undefined') return false;
	const model = readPageModel();
	const route = resolveInitialRoute(window.location.pathname);
	if (!model || route?.kind !== 'chapter' || root.querySelector('#view-article.active') === null) return false;
	const runtime = getRuntime(root);
	if (runtime?.activeCourseId !== route.slug || runtime.activeChapterIndex !== route.chapter) return false;
	const remainsOnOriginalSsrChapter = model.pageType === 'chapter' && model.topic.slug === route.slug && model.chapter.index === route.chapter;
	if (!remainsOnOriginalSsrChapter) return true;
	// A Chapter can move away and return before auth hydration finishes. Its
	// original route then matches the PageModel again, but the SPA transition has
	// already replaced the server body with a pending shell. Retry only that
	// pending body; ready and locked server output remains untouched.
	return root.querySelector('#article-body')?.getAttribute('data-state') === 'loading';
}

export function installBootstrapCapabilities(runtime: Pick<PrototypeRuntime, 'crossCycleUpgradeEnabled' | 'topicTrendingV1Enabled' | 'topicNewestV1Enabled' | 'audioBackgroundHandoffEnabled'>, payload: unknown): void {
	runtime.crossCycleUpgradeEnabled = false;
	runtime.topicTrendingV1Enabled = false;
	runtime.topicNewestV1Enabled = false;
	runtime.audioBackgroundHandoffEnabled = false;
	if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;
	const capabilities = (payload as Record<string, unknown>).capabilities;
	if (!capabilities || typeof capabilities !== 'object' || Array.isArray(capabilities)) return;
	const value = (capabilities as Record<string, unknown>).crossCycleUpgrade;
	runtime.crossCycleUpgradeEnabled = typeof value === 'boolean' ? value : false;
	const trending = (capabilities as Record<string, unknown>).topicTrendingV1;
	runtime.topicTrendingV1Enabled = typeof trending === 'boolean' ? trending : false;
	const newest = (capabilities as Record<string, unknown>).topicNewestV1;
	runtime.topicNewestV1Enabled = typeof newest === 'boolean' ? newest : false;
	const handoff = (capabilities as Record<string, unknown>).audioBackgroundHandoff;
	runtime.audioBackgroundHandoffEnabled = typeof handoff === 'boolean' ? handoff : false;
}

/** Capability resets are synchronous so stale mounted controls fail closed during every hydration network window. */
export function resetCrossCycleUpgradeCapability(root: Root, state: PrototypeState): void {
	state.runtime.crossCycleUpgradeEnabled = false;
	state.runtime.topicTrendingV1Enabled = false;
	state.runtime.topicNewestV1Enabled = false;
	state.runtime.audioBackgroundHandoffEnabled = false;
	setMountedSubscriptionChooserCapability(root, false);
}

function confirmedSnapshotMatchesState(state: PrototypeState, snapshot: SessionSnapshot, change: PendingChange): boolean {
	return snapshot.viewer.authStatus === 'authenticated'
		&& snapshot.viewer.userId === change.initiatingUserId
		&& snapshot.viewer.sessionHash === change.initiatingSessionHash
		&& state.runtime.userId === change.initiatingUserId
		&& state.runtime.sessionHash === change.initiatingSessionHash
		&& snapshot.viewer.planContext?.billingPlan === change.expectedPlan
		&& snapshot.viewer.billingPlan === change.expectedPlan
		&& snapshot.subscription?.providerSubscriptionId === change.providerSubscriptionId
		&& snapshot.subscription.plan === change.expectedPlan
		&& snapshot.subscription.billingCycle === change.expectedBillingCycle;
}

async function applyConfirmedBillingSnapshot(
	root: Root,
	state: PrototypeState,
	snapshot: SessionSnapshot,
	change: PendingChange,
): Promise<boolean> {
	if (!confirmedSnapshotMatchesState(state, snapshot, change) || !snapshot.viewer.planContext || !snapshot.subscription) return false;
	resetCrossCycleUpgradeCapability(root, state);
	let bootstrapPayload: unknown = null;
	try {
		bootstrapPayload = await state.runtime.api.bootstrap(snapshot.viewer.planContext.effectiveAccessPlan);
	} catch (error) {
		recordRuntimeError(state.runtime, error);
	}
	// Network completion can race logout/account switching. Never apply the old
	// account's otherwise-valid paid snapshot to the new controller identity.
	if (!confirmedSnapshotMatchesState(state, snapshot, change)) return false;

	const planContext = snapshot.viewer.planContext;
	state.planContext = planContext;
	state.currentPlan = planContext.billingPlan;
	state.effectiveAccessPlan = planContext.effectiveAccessPlan;
	state.runtime.currentPlan = planContext.effectiveAccessPlan;
	state.runtime.billingCycle = snapshot.subscription.billingCycle;
	state.runtime.userId = snapshot.viewer.userId ?? '';
	state.runtime.sessionHash = snapshot.viewer.sessionHash ?? '';
	state.runtime.userEmail = snapshot.viewer.email ?? '';
	state.runtime.userDisplayName = snapshot.viewer.displayName ?? '';
	installBootstrapPayload(root, state.runtime, bootstrapPayload, {
		userId: snapshot.viewer.userId,
		sessionHash: snapshot.viewer.sessionHash,
	});
	syncTopicViewerCacheScope(state.runtime, true);
	invalidateTopicIndex();
	hideLoginState(root, state);
	if (state.runtime.userId && state.runtime.userEmail) startVideoWatermark(root);
	else stopVideoWatermark();
	refreshMountedSubscriptionChoosers(root, state, snapshot.subscription);
	const applied = confirmedSnapshotMatchesState(state, snapshot, change)
		&& state.currentPlan === change.expectedPlan
		&& state.planContext.billingPlan === change.expectedPlan
		&& state.runtime.billingCycle === change.expectedBillingCycle;
	if (applied) schedulePostUpgradePageRefresh();
	return applied;
}

function installBootstrapPayload(
	root: Root,
	runtime: PrototypeRuntime,
	payload: unknown,
	identity: { userId: string | null; sessionHash: string | null },
): (Record<string, unknown> & { viewer: { userId: string | null; email?: string | null; sessionHash?: string | null } }) | null {
	runtime.crossCycleUpgradeEnabled = false;
	runtime.topicTrendingV1Enabled = false;
	runtime.topicNewestV1Enabled = false;
	const boot = trustedBootstrapPayload(payload, identity);
	if (!boot) return null;
	installBootstrapCapabilities(runtime, boot);
	if (Array.isArray(boot.plans) || (boot.plans && typeof boot.plans === 'object')) {
		planStore.setPlans(boot.plans as never, { catalogExpiresAt: typeof boot.catalogExpiresAt === 'string' ? boot.catalogExpiresAt : null });
	}
	if (boot.settings) siteSettingsStore.set(boot.settings as never);
	if (Array.isArray(boot.categories)) {
		categoriesStore.set(boot.categories as never);
		hydrateCategoryButtons(root);
	}
	return boot;
}

function trustedBootstrapPayload(
	payload: unknown,
	identity: { userId: string | null; sessionHash: string | null },
): (Record<string, unknown> & { viewer: { userId: string | null; email?: string | null; sessionHash?: string | null } }) | null {
	if (!isViewerPayload(payload) || payload.viewer.userId !== identity.userId) return null;
	if ((payload.viewer.sessionHash ?? null) !== identity.sessionHash) return null;
	return payload;
}

/** Public and gate choosers can quote the catalog immediately. They never
 * receive a guessed paid subscription; only the opened paywall reads that
 * private fact before enabling a paid-member checkout path. */
function mountPublicSubscriptionChoosers(root: Root, state: PrototypeState): void {
	for (const [prefix, source] of [
		['pricing', 'pricing'],
		['gate', 'ai-gate'],
		['ghGate', 'github-gate'],
		['ghGateSocial', 'github-gate'],
	] as const) {
		if (!root.querySelector<HTMLElement>(`[data-prefix="${prefix}"]`)) continue;
		const context = subscriptionChooserContextFromState(state, { source });
		if (context) mountSubscriptionChooser(root, { prefix, context })?.reset(context);
	}
}

/** Only apply subscription facts to an already-open chooser after the
 * entitlement refresh has verified a user-initiated billing mutation. */
function refreshMountedSubscriptionChoosers(
	root: Root,
	state: PrototypeState,
	subscription: {
		providerSubscriptionId?: string;
		plan: string;
		billingCycle: string;
		currentPeriodEnd: string | null;
		cancelAtPeriodEnd: boolean;
	} | null,
): void {
	for (const [prefix, source] of [
		['pw', 'paywall'],
		['gate', 'ai-gate'],
		['ghGate', 'github-gate'],
		['ghGateSocial', 'github-gate'],
		['pricing', 'pricing'],
	] as const) {
		if (!root.querySelector<HTMLElement>(`[data-prefix="${prefix}"]`)) continue;
		const context = subscriptionChooserContextFromState(state, { source, subscription });
		if (context) updateSubscriptionChooser(root, prefix, context);
	}
}

export function showVisitorLoginGate(root: Root, state: PrototypeState): void {
  showLoginState(root, state);
}

interface StartupSnapshot {
	generation: number;
	isLoggedIn: boolean;
	currentPlan: PlanCode;
	effectiveAccessPlan: PlanCode;
	planContext: string;
	userId: string;
	userEmail: string;
	userDisplayName: string;
	sessionHash: string;
}

function beginStartupSnapshot(root: Root, state: PrototypeState): StartupSnapshot {
	const generation = (hydrationGenerations.get(root as object) ?? 0) + 1;
	hydrationGenerations.set(root as object, generation);
	return {
		generation,
		isLoggedIn: state.isLoggedIn,
		currentPlan: state.currentPlan,
		effectiveAccessPlan: state.effectiveAccessPlan,
		planContext: planContextFingerprint(state.planContext),
		userId: state.runtime.userId ?? '',
		userEmail: state.runtime.userEmail ?? '',
		userDisplayName: state.runtime.userDisplayName ?? '',
		sessionHash: state.runtime.sessionHash ?? '',
	};
}

function ownsStartupSnapshot(root: Root, state: PrototypeState, snapshot: StartupSnapshot): boolean {
	return hydrationGenerations.get(root as object) === snapshot.generation
		&& state.isLoggedIn === snapshot.isLoggedIn
		&& state.currentPlan === snapshot.currentPlan
		&& state.effectiveAccessPlan === snapshot.effectiveAccessPlan
		&& planContextFingerprint(state.planContext) === snapshot.planContext
		&& (state.runtime.userId ?? '') === snapshot.userId
		&& (state.runtime.userEmail ?? '') === snapshot.userEmail
		&& (state.runtime.userDisplayName ?? '') === snapshot.userDisplayName
		&& (state.runtime.sessionHash ?? '') === snapshot.sessionHash;
}

function bootstrapMatchesSession(payload: unknown, sessionViewer: SessionPayload['viewer']): boolean {
	if (!isViewerPayload(payload)) return false;
	return viewerFingerprint(payload.viewer) === viewerFingerprint(sessionViewer);
}

function viewerFingerprint(viewer: unknown): string | null {
	if (!viewer || typeof viewer !== 'object' || Array.isArray(viewer)) return null;
	const source = viewer as Record<string, unknown>;
	if (!(source.authStatus === 'visitor' || source.authStatus === 'authenticated')) return null;
	if (typeof source.plan !== 'string') return null;
	if (!(source.userId === null || typeof source.userId === 'string')) return null;
	if (!(source.email === null || typeof source.email === 'string')) return null;
	if (!(source.displayName === undefined || source.displayName === null || typeof source.displayName === 'string')) return null;
	if (!(source.sessionHash === undefined || source.sessionHash === null || typeof source.sessionHash === 'string')) return null;
	return JSON.stringify([
		source.authStatus,
		source.plan,
		source.userId,
		source.email,
		source.displayName ?? null,
		source.sessionHash ?? null,
		planContextFingerprint(source.planContext),
	]);
}

function isLocalStartupOrigin(): boolean {
	const hostname = globalThis.location?.hostname?.toLowerCase() ?? '';
	return hostname === 'localhost' || hostname.endsWith('.localhost') || hostname === '127.0.0.1' || hostname === '::1';
}

function planContextFingerprint(value: unknown): string {
	const context = parseClientPlanContext(value);
	if (context) return JSON.stringify(context);
	return stableValueFingerprint(value);
}

function stableValueFingerprint(value: unknown): string {
	if (value === null) return 'null';
	if (value === undefined) return 'undefined';
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return JSON.stringify(value);
	if (Array.isArray(value)) return `[${value.map(stableValueFingerprint).join(',')}]`;
	if (typeof value !== 'object') return typeof value;
	return `{${Object.keys(value as Record<string, unknown>).sort().map((key) => `${JSON.stringify(key)}:${stableValueFingerprint((value as Record<string, unknown>)[key])}`).join(',')}}`;
}

function retainHomeFirstFrameForStartupMismatch(root: Root, state: PrototypeState, options: HydrationOptions): void {
	if (!options.preserveSsrView && isActiveHomeRoute(root)) {
		recordSessionCheckGaveUp(HOME_SESSION_AUTO_RETRIES);
		retainHomeFirstFrameForSessionRetry(root, state, { ...options, sessionAutoRetryAttempt: HOME_SESSION_AUTO_RETRIES });
	}
}

function retainHomeFirstFrameForSessionRetry(
  root: Root,
  state: PrototypeState,
  options: HydrationOptions,
): void {
  const home = byId(root, 'view-home');
  const workspace = byId(root, 'memberWorkspaceSection');
  const retry = byId(root, 'homeSessionRetry') as HTMLButtonElement | null;
  if (!home || !workspace || !retry) return;
  home.dataset.auth = 'pending';
	home.dataset.plan = state.effectiveAccessPlan;
  workspace.setAttribute('aria-busy', 'false');
  retry.hidden = false;
  retry.onclick = () => {
    retry.hidden = true;
    retry.disabled = true;
    workspace.setAttribute('aria-busy', 'true');
    // Drop the spent auto-retry counter so a manual retry gets a fresh budget.
    void hydratePrototypeRuntime(root, state, { preserveSsrView: options.preserveSsrView === true }).finally(() => {
      retry.disabled = false;
    });
  };
}

function isActiveHomeRoute(root: Root): boolean {
  const home = byId(root, 'view-home');
  if (!home?.classList.contains('active')) return false;
  const pathname = globalThis.location?.pathname;
  return !pathname || pathname === '/';
}

function setHomeWorkspaceBusy(root: Root, busy: boolean): void {
  byId(root, 'memberWorkspaceSection')?.setAttribute('aria-busy', String(busy));
}

function isViewerPayload(value: unknown): value is Record<string, unknown> & {
	viewer: { userId: string | null; email?: string | null; sessionHash?: string | null };
} {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
	const viewer = (value as Record<string, unknown>).viewer;
	if (!viewer || typeof viewer !== 'object' || Array.isArray(viewer)) return false;
	if (!Object.prototype.hasOwnProperty.call(viewer, 'userId')) return false;
	const record = viewer as Record<string, unknown>;
	return (record.userId === null || typeof record.userId === 'string')
		&& (record.sessionHash === undefined || record.sessionHash === null || typeof record.sessionHash === 'string');
}
